import React, { useState, useEffect } from 'react';
import timeAgo from '../lib/timeAgo';
import { useAuth, API } from '../App';
import { useT } from '../i18n';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import ConfirmDialog from './ConfirmDialog';
import HelpHint from './HelpHint';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Plus,
  Eye,
  Edit,
  Trash2,
  Copy,
  ExternalLink,
  MousePointer,
  Users,
  TrendingUp,
  MoreHorizontal,
  Search,
  Play,
  Pause,
  X,
  Archive,
  ArchiveRestore,
  Target,
  DollarSign,
  Download,
  ArrowUpDown,
  LayoutDashboard,
  List,
  Save,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import axios from 'axios';
import { toast } from 'sonner';
import XpnsrPageHeader from './XpnsrPageHeader';
import BRAND from '../brand';
import XpnsrEmpty from './XpnsrEmpty';
import useLoadingAction from '../hooks/useLoadingAction';
import FrameCheck from './FrameCheck';

const CAMP_VIEW_KEY = 'xpnsr.campaigns.view';
const CAMP_VIEWS_KEY = 'xpnsr.campaigns.savedViews';

const loadLS = (key, fallback) => {
  try {
    const raw = localStorage.getItem(key);
    return raw == null ? fallback : JSON.parse(raw);
  } catch (_e) {
    return fallback;
  }
};
const saveLS = (key, value) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (_e) {
    /* ignore quota / private-mode errors */
  }
};

export default function Campaigns() {
  const { user } = useAuth();
  const { t } = useT();
  const [searchParams, setSearchParams] = useSearchParams();
  const [campaigns, setCampaigns] = useState([]);
  const [trafficSources, setTrafficSources] = useState([]);
  const [domains, setDomains] = useState([]);
  const [whitePages, setWhitePages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState(searchParams.get('q') || '');
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || 'all');
  const [statsDays, setStatsDays] = useState('7');
  const [summaryMap, setSummaryMap] = useState({});
  const stat = (id) => summaryMap[id] || {};
  const [showCreateDialog, setShowCreateDialog] = useState(searchParams.get('new') === '1');
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [sortKey, setSortKey] = useState('name');
  const [sortDir, setSortDir] = useState('asc');
  const [viewMode, setViewMode] = useState(() => loadLS(CAMP_VIEW_KEY, 'cards'));
  const [savedViews, setSavedViews] = useState(() => {
    const v = loadLS(CAMP_VIEWS_KEY, []);
    return Array.isArray(v) ? v : [];
  });
  const [saveViewOpen, setSaveViewOpen] = useState(false);
  const [newViewName, setNewViewName] = useState('');

  const changeView = (mode) => {
    setViewMode(mode);
    saveLS(CAMP_VIEW_KEY, mode);
  };
  // A saved view is a named snapshot of the current filter / sort / layout so
  // an operator can flip between "Tier-1 by revenue (table)" and "all active
  // (cards)" in one click. Persisted per browser in localStorage.
  const applyView = (v) => {
    if (!v) return;
    setSearchTerm(v.search || '');
    setStatusFilter(v.status || 'all');
    setGroupFilter(v.group || 'all');
    setStatsDays(v.days || '7');
    setSortKey(v.sortKey || 'name');
    setSortDir(v.sortDir || 'asc');
    if (v.view) changeView(v.view);
  };
  const saveCurrentView = () => {
    const name = newViewName.trim();
    if (!name) return;
    const entry = {
      name,
      search: searchTerm,
      status: statusFilter,
      group: groupFilter,
      days: statsDays,
      sortKey,
      sortDir,
      view: viewMode,
    };
    setSavedViews((prev) => {
      // Overwrite a same-named view rather than duplicating it.
      const next = [...prev.filter((x) => x.name !== name), entry];
      saveLS(CAMP_VIEWS_KEY, next);
      return next;
    });
    setNewViewName('');
    setSaveViewOpen(false);
    toast.success(t('View saved'));
  };
  const deleteView = (name) => {
    setSavedViews((prev) => {
      const next = prev.filter((x) => x.name !== name);
      saveLS(CAMP_VIEWS_KEY, next);
      return next;
    });
  };

  // Sync search query + status filter → URL
  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    if (searchTerm) next.set('q', searchTerm); else next.delete('q');
    if (statusFilter && statusFilter !== 'all') next.set('status', statusFilter); else next.delete('status');
    if (next.toString() !== searchParams.toString()) {
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line
  }, [searchTerm, statusFilter]);

  // If URL says ?new=1 strip it after opening dialog
  useEffect(() => {
    if (searchParams.get('new') === '1') {
      const next = new URLSearchParams(searchParams);
      next.delete('new');
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line
  }, []);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    target_url: '',
    traffic_source_id: '',
    domain_id: '',
    cost_model: 'CPC',
    cost_value: 0,
    traffic_loss_percent: 0,
    cost_currency: 'USD',
    cost_auto_update: false,
    split_testing_enabled: false,
    bind_visitors: false,
    bind_visitors_ttl_days: 30,
    uniqueness_method: 'cookie',
    uniqueness_param: '',
    click_id_param: '',
    cookieless: false,
    pseudonymize_ip: false,
    redirect_mode: '302',
    cloaking_enabled: false,
    cloaking_safe_url: '',
    cloaking_white_page_id: '',
    cloaking_white_page_ids: [],
    cloak_bots: true,
    cloak_crawlers: true,
    cloak_datacenter: true,
    cloak_proxy: true,
    cloak_require_click_id: false,
    group: '',
  });
  const [groupFilter, setGroupFilter] = useState('all');

  useEffect(() => {
    fetchCampaigns();
    fetchTrafficSources();
    fetchDomains();
    fetchWhitePages();
  }, []);

  useEffect(() => {
    let cancelled = false;
    axios
      .get(API + '/analytics/campaigns-summary', { params: { days: Number(statsDays) } })
      .then((r) => {
        if (cancelled) return;
        const map = {};
        (r.data || []).forEach((row) => {
          map[row.campaign_id] = row;
        });
        setSummaryMap(map);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [statsDays]);

  const fetchCampaigns = async () => {
    try {
      const response = await axios.get(`${API}/campaigns`, {
        params: { include_archived: statusFilter === 'archived' },
      });
      setCampaigns(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching campaigns:', error);
      toast.error(t('Error loading campaigns'));
      setLoading(false);
    }
  };

  const fetchTrafficSources = async () => {
    try {
      const response = await axios.get(`${API}/traffic-sources`);
      setTrafficSources(response.data);
    } catch (error) {
      console.error('Error fetching traffic sources:', error);
    }
  };

  const fetchDomains = async () => {
    try {
      const response = await axios.get(`${API}/domains`);
      // Only verified domains are usable for tracker links.
      const verified = (response.data || []).filter((d) => d.status === 'verified');
      setDomains(verified);
    } catch (error) {
      console.error('Error fetching domains:', error);
    }
  };

  const fetchWhitePages = async () => {
    try {
      const response = await axios.get(`${API}/white-pages`);
      setWhitePages(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error fetching white pages:', error);
    }
  };

  const resetForm = () =>
    setFormData({
      name: '',
      description: '',
      target_url: '',
      traffic_source_id: '',
      domain_id: '',
      cost_model: 'CPC',
      cost_value: 0,
      traffic_loss_percent: 0,
      cost_currency: 'USD',
      cost_auto_update: false,
      split_testing_enabled: false,
      bind_visitors: false,
      bind_visitors_ttl_days: 30,
      uniqueness_method: 'cookie',
      uniqueness_param: '',
      click_id_param: '',
      cookieless: false,
      pseudonymize_ip: false,
      redirect_mode: '302',
      cloaking_enabled: false,
      cloaking_safe_url: '',
      cloak_bots: true,
      cloak_crawlers: true,
      cloak_datacenter: true,
      cloak_proxy: true,
      cloak_require_click_id: false,
      group: '',
    });

  const isValidHttpUrl = (u) => {
    try {
      const parsed = new URL(String(u || '').trim());
      return parsed.protocol === 'http:' || parsed.protocol === 'https:';
    } catch {
      return false;
    }
  };

  // Shared cloaking config block for the create + edit dialogs. Self-hosted
  // white/money-page split — no third-party service. Sub-fields appear only
  // once cloaking is enabled (progressive disclosure).
  const renderCloakingSection = (idp) => (
    <div className="space-y-3 rounded-md border border-border p-3">
      <label className="flex items-center gap-2 text-sm font-medium">
        <input
          type="checkbox"
          className="xp-checkbox"
          checked={!!formData.cloaking_enabled}
          onChange={(e) => setFormData({ ...formData, cloaking_enabled: e.target.checked })}
          data-testid={`${idp}-campaign-cloaking-enabled`}
        />
        <span>{t('Cloaking — send bots to a safe page')}</span>
        <HelpHint text={t('Self-hosted cloaking. Bots, ad-network moderators, datacenter/proxy IPs and search crawlers see the safe (white) page; genuine humans go to the real offer. Classification uses the tracker\'s own bot / IP / crawler-DNS checks — no third-party service.')} />
      </label>
      {formData.cloaking_enabled && (
        <div className="space-y-3 pl-6">
          <div className="space-y-1">
            <div className="flex items-center justify-between">
              <Label>{t('White pages (hosted, same-origin)')}</Label>
              {(formData.cloaking_white_page_ids || []).length > 0 && (
                <span className="text-xs text-muted-foreground">
                  {(formData.cloaking_white_page_ids || []).length} {t('selected — rotated per request')}
                </span>
              )}
            </div>
            {whitePages.length === 0 ? (
              <p className="text-xs text-muted-foreground">
                {t('No white pages yet. Upload some under White pages, then pick them here.')}
              </p>
            ) : (
              <div className="max-h-40 overflow-y-auto rounded-md border border-border divide-y divide-border">
                {whitePages.map((w) => {
                  const ids = formData.cloaking_white_page_ids || [];
                  const checked = ids.includes(w.id);
                  return (
                    <label key={w.id} className="flex items-center gap-2 px-2 py-1.5 text-sm cursor-pointer hover:bg-muted">
                      <input
                        type="checkbox"
                        className="xp-checkbox"
                        checked={checked}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            cloaking_white_page_id: '',
                            cloaking_white_page_ids: e.target.checked
                              ? [...ids, w.id]
                              : ids.filter((x) => x !== w.id),
                          })
                        }
                        data-testid={`${idp}-campaign-cloaking-white-${w.id}`}
                      />
                      <span className="truncate">{w.name}</span>
                      {w.tags?.length > 0 && (
                        <span className="ml-auto flex gap-1 flex-shrink-0">
                          {w.tags.slice(0, 2).map((tg) => (
                            <Badge key={tg} variant="outline" className="text-[10px]">{tg}</Badge>
                          ))}
                        </span>
                      )}
                    </label>
                  );
                })}
              </div>
            )}
            <p className="text-xs text-muted-foreground">
              {t('Optional — the most robust option for Google/Facebook: served from your own campaign domain (/wp/…), so the final URL stays on-domain. Select several to rotate a random one per request, so one white isn’t reused across hundreds of ads. Manage under White pages.')}
            </p>
          </div>
          <div className="space-y-1">
            <Label htmlFor={`${idp}-cloaking_safe_url`}>{t('Safe page URL (external)')}</Label>
            <Input
              id={`${idp}-cloaking_safe_url`}
              type="url"
              placeholder="https://example.com/white-page"
              value={formData.cloaking_safe_url || ''}
              onChange={(e) => setFormData({ ...formData, cloaking_safe_url: e.target.value })}
              data-testid={`${idp}-campaign-cloaking-safe-url`}
            />
            <p className="text-xs text-muted-foreground">
              {t('External safe page — the tracker proxies it same-origin (identity tags stripped). Used when no white page is selected. Must be an absolute http(s) URL. This method already passes Google review on its own; a hosted white page is just an extra-robust alternative served fully on your domain.')}
            </p>
          </div>
          <div className="grid grid-cols-2 gap-2">
            {[
              ['cloak_bots', t('Bots & spoofed crawlers'), t('UA-pattern bots and UAs that fake a search-crawler identity (failed DNS check) get the safe page.')],
              ['cloak_crawlers', t('Verified search crawlers'), t('DNS-verified search crawlers (Googlebot/Bingbot/…) get the safe page. Turn off to let them index the real offer.')],
              ['cloak_datacenter', t('Datacenter / hosting VPN / Tor IPs'), t('Visitors from cloud and hosting networks, VPNs that run on them, and Tor exits get the safe page. Residential proxies cannot be recognised without a paid IP database.')],
              ['cloak_require_click_id', t('Offer only with a Google Ads click ID'), t('Real Google Ads clicks always carry gclid, gbraid or wbraid; reviewers and link checkers open the link without one. Visitors without a valid click ID get the safe page. Leave off if auto-tagging is disabled or the campaign gets other traffic.')],
            ].map(([key, label, hint]) => (
              <label key={key} className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  className="xp-checkbox"
                  checked={formData[key] !== false}
                  onChange={(e) => setFormData({ ...formData, [key]: e.target.checked })}
                  data-testid={`${idp}-campaign-${key.replace(/_/g, '-')}`}
                />
                <span>{label}</span>
                <HelpHint text={hint} />
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  const availableGroups = [...new Set(campaigns.map((c) => c.group).filter(Boolean))].sort();

  const handleCreateCampaign = async (e) => {
    e.preventDefault();
    if (creating) return;
    if (
      formData.cloaking_enabled &&
      !(formData.cloaking_white_page_ids && formData.cloaking_white_page_ids.length) &&
      !formData.cloaking_white_page_id &&
      !isValidHttpUrl(formData.cloaking_safe_url)
    ) {
      toast.error(t('Select a white page or enter a valid http(s) Safe page URL for cloaking'));
      return;
    }
    setCreating(true);
    try {
      const response = await axios.post(`${API}/campaigns`, formData);
      setCampaigns([response.data, ...campaigns]);
      setShowCreateDialog(false);
      resetForm();
      toast.success(t('Campaign created successfully!'));
    } catch (error) {
      console.error('Error creating campaign:', error);
      toast.error(error.response?.data?.detail || t('Error creating campaign'));
    } finally {
      setCreating(false);
    }
  };

  const handleUpdateCampaign = async (e) => {
    e.preventDefault();
    if (!editingCampaign || saving) return;
    if (
      formData.cloaking_enabled &&
      !(formData.cloaking_white_page_ids && formData.cloaking_white_page_ids.length) &&
      !formData.cloaking_white_page_id &&
      !isValidHttpUrl(formData.cloaking_safe_url)
    ) {
      toast.error(t('Select a white page or enter a valid http(s) Safe page URL for cloaking'));
      return;
    }
    setSaving(true);
    try {
      const response = await axios.put(`${API}/campaigns/${editingCampaign.id}`, formData);
      setCampaigns(campaigns.map((c) => (c.id === editingCampaign.id ? response.data : c)));
      setShowEditDialog(false);
      setEditingCampaign(null);
      resetForm();
      toast.success(t('Campaign updated successfully!'));
    } catch (error) {
      console.error('Error updating campaign:', error);
      toast.error(error.response?.data?.detail || t('Error updating campaign'));
    } finally {
      setSaving(false);
    }
  };

  // ----- Selection helpers -----
  const toggleSelected = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const clearSelection = () => setSelectedIds(new Set());
  const selectAllVisible = (visibleCampaigns) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      visibleCampaigns.forEach((c) => next.add(c.id));
      return next;
    });
  };
  const allVisibleSelected = (visibleCampaigns) =>
    visibleCampaigns.length > 0 && visibleCampaigns.every((c) => selectedIds.has(c.id));

  // ----- Bulk actions -----
  // Wave 20 — talk to the dedicated bulk endpoints instead of
  // firing N parallel single-row requests. The server applies the
  // mutation atomically (one update_many / delete_many), enforces
  // RBAC uniformly, and writes a single audit row for the batch.
  const bulkSetActive = async (isActive) => {
    if (selectedIds.size === 0 || bulkBusy) return;
    setBulkBusy(true);
    const ids = Array.from(selectedIds);
    try {
      const r = await axios.post(`${API}/campaigns/bulk-status`, {
        action: isActive ? 'activate' : 'pause',
        ids,
      });
      const matched = new Set(r.data?.matched_ids || []);
      setCampaigns((prev) =>
        prev.map((c) => (matched.has(c.id) ? { ...c, is_active: isActive } : c)),
      );
      const okCount = r.data?.matched_count || 0;
      const missing = r.data?.missing_count || 0;
      if (okCount > 0) {
        toast.success(
          isActive
            ? t('Activated {n}').replace('{n}', String(okCount))
            : t('Paused {n}').replace('{n}', String(okCount)),
        );
      }
      if (missing > 0) {
        toast.warning(
          t('Skipped {n} (no access or not found)').replace(
            '{n}',
            String(missing),
          ),
        );
      }
      if (missing === 0) clearSelection();
    } catch (error) {
      const detail = error?.response?.data?.detail || t('Bulk update failed');
      toast.error(detail);
    } finally {
      setBulkBusy(false);
    }
  };
  const bulkDelete = async () => {
    if (selectedIds.size === 0 || bulkBusy) return;
    setBulkDeleteOpen(false);
    setBulkBusy(true);
    const ids = Array.from(selectedIds);
    try {
      const r = await axios.delete(`${API}/campaigns/bulk`, {
        data: { ids },
      });
      const matched = new Set(r.data?.matched_ids || []);
      setCampaigns((prev) => prev.filter((c) => !matched.has(c.id)));
      const okCount = r.data?.matched_count || 0;
      const missing = r.data?.missing_count || 0;
      if (okCount > 0) {
        toast.success(t('Deleted {n}').replace('{n}', String(okCount)));
      }
      if (missing > 0) {
        toast.warning(
          t('Skipped {n} (no access or not found)').replace(
            '{n}',
            String(missing),
          ),
        );
      }
      if (missing === 0) clearSelection();
    } catch (error) {
      const detail = error?.response?.data?.detail || t('Bulk delete failed');
      toast.error(detail);
    } finally {
      setBulkBusy(false);
    }
  };

  const quickToggleStatus = async (campaign) => {
    const newActive = !campaign.is_active;
    try {
      await axios.put(`${API}/campaigns/${campaign.id}`, { ...campaign, is_active: newActive });
      setCampaigns(prev => prev.map(c => c.id === campaign.id ? { ...c, is_active: newActive } : c));
      toast.success(newActive ? t('Campaign activated') : t('Campaign paused'));
    } catch {
      toast.error(t('Failed to update status'));
    }
  };

  const handleCloneCampaign = async (campaignId) => {
    try {
      const response = await axios.post(`${API}/campaigns/${campaignId}/clone`);
      setCampaigns([response.data, ...campaigns]);
      toast.success(t('Campaign cloned successfully!'));
    } catch (error) {
      console.error('Error cloning campaign:', error);
      toast.error(error.response?.data?.detail || t('Error cloning campaign'));
    }
  };

  const handleDeleteCampaign = async (campaignId) => {
    try {
      await axios.delete(`${API}/campaigns/${campaignId}`);
      setCampaigns(campaigns.filter((c) => c.id !== campaignId));
      setDeleteTarget(null);
      toast.success(t('Campaign deleted successfully!'));
    } catch (error) {
      console.error('Error deleting campaign:', error);
      toast.error(t('Error deleting campaign'));
    }
  };

  // URL parameter rotation (audit Phase 2 §6.2.А). The tracker accepts every
  // alias in this list as an equivalent entry point; operators rotate which
  // path their ad creative points at to evade traffic-source URL fingerprints.
  // Keep in sync with backend/redirect_aliases.py KNOWN_ALIASES.
  const TRACKER_ALIASES = ['r', 'go', 'click', 'track', 'c'];

  // Build the public tracking URL for a campaign. If the campaign is bound to
  // a verified custom domain, construct https://<hostname>/<alias>/<tid>;
  // otherwise fall back to the API host's /<alias>/ path which still works
  // for the workspace default domain.
  const buildTrackingUrl = (campaign, alias = 'r') => {
    const safeAlias = TRACKER_ALIASES.includes(alias) ? alias : 'r';
    const domainId = campaign?.domain_id;
    if (domainId) {
      const domain = domains.find((d) => d.id === domainId);
      if (domain?.hostname) {
        return `https://${domain.hostname}/${safeAlias}/${campaign.tracking_id}`;
      }
    }
    // API typically points at https://api.example.com — strip the /api suffix
    // so /<alias>/ lands on the public route, not the namespaced router.
    const apiBase = String(API || '').replace(/\/api\/?$/, '');
    return `${apiBase}/${safeAlias}/${campaign.tracking_id}`;
  };

  const copyTrackingUrl = (campaign, alias = 'r') => {
    const base = buildTrackingUrl(campaign, alias);
    const qs = (campaign && campaign.params ? campaign.params : [])
      // Keys without a default (tm, tml, a…) come from the ad itself (e.g. the
      // Google Ads Final URL suffix); a literal {key} would be sent as is.
      .filter((p) => p && p.key && p.placeholder)
      .map((p) => p.key + '=' + p.placeholder)
      .join('&');
    const trackingUrl = qs ? base + '?' + qs : base;
    navigator.clipboard
      .writeText(trackingUrl)
      .then(() => toast.success(t('Link copied to clipboard!')))
      .catch(() => toast.error(t('Failed to copy link')));
  };

  const openEditDialog = (campaign) => {
    setShowCreateDialog(false);
    setEditingCampaign(campaign);
    setFormData({
      name: campaign.name,
      description: campaign.description || '',
      target_url: campaign.target_url,
      traffic_source_id: campaign.traffic_source_id || '',
      domain_id: campaign.domain_id || '',
      cost_model: campaign.cost_model || 'CPC',
      group: campaign.group || '',
      cost_value: campaign.cost_value ?? 0,
      traffic_loss_percent: campaign.traffic_loss_percent ?? 0,
      cost_currency: campaign.cost_currency || 'USD',
      cost_auto_update: campaign.cost_auto_update || false,
      split_testing_enabled: campaign.split_testing_enabled || false,
      bind_visitors: campaign.bind_visitors || false,
      bind_visitors_ttl_days: campaign.bind_visitors_ttl_days ?? 30,
      uniqueness_method: campaign.uniqueness_method || 'cookie',
      uniqueness_param: campaign.uniqueness_param || '',
      click_id_param: campaign.click_id_param || '',
      cookieless: campaign.cookieless || false,
      pseudonymize_ip: campaign.pseudonymize_ip || false,
      redirect_mode: campaign.redirect_mode || '302',
      cloaking_enabled: campaign.cloaking_enabled || false,
      cloaking_safe_url: campaign.cloaking_safe_url || '',
      cloaking_white_page_id: '',
      cloaking_white_page_ids:
        campaign.cloaking_white_page_ids && campaign.cloaking_white_page_ids.length
          ? campaign.cloaking_white_page_ids
          : campaign.cloaking_white_page_id
            ? [campaign.cloaking_white_page_id]
            : [],
      cloak_bots: campaign.cloak_bots !== false,
      cloak_crawlers: campaign.cloak_crawlers !== false,
      cloak_datacenter: campaign.cloak_datacenter !== false,
      cloak_proxy: campaign.cloak_proxy !== false,
      cloak_require_click_id: campaign.cloak_require_click_id === true,
    });
    setShowEditDialog(true);
  };

  const openCreateDialog = () => {
    setShowEditDialog(false);
    setEditingCampaign(null);
    resetForm();
    setShowCreateDialog(true);
  };

  const handleArchive = async (campaign) => {
    try {
      const res = await axios.post(`${API}/campaigns/${campaign.id}/archive`);
      if (res.data.is_archived) {
        setCampaigns((prev) => prev.filter((c) => c.id !== campaign.id));
        toast.success(t('Campaign archived'));
      } else {
        setCampaigns((prev) =>
          prev.map((c) => (c.id === campaign.id ? { ...c, is_archived: false } : c)),
        );
        toast.success(t('Campaign restored'));
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || t('Error archiving campaign'));
    }
  };

  // Refetch when switching to/from archived view
  useEffect(() => {
    fetchCampaigns();
    // eslint-disable-next-line
  }, [statusFilter === 'archived']);

  const filteredCampaigns = campaigns.filter((campaign) => {
    const matchesSearch =
      campaign.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      campaign.description?.toLowerCase().includes(searchTerm.toLowerCase());
    if (!matchesSearch) return false;
    if (groupFilter !== 'all') {
      if (groupFilter === '__none__') {
        if (campaign.group) return false;
      } else if (campaign.group !== groupFilter) return false;
    }
    if (statusFilter === 'archived') return campaign.is_archived === true;
    if (statusFilter === 'active') return campaign.is_active === true && !campaign.is_archived;
    if (statusFilter === 'paused') return campaign.is_active === false && !campaign.is_archived;
    return !campaign.is_archived;
  });

  const toggleSort = (key) => {
    if (sortKey === key) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  // Sort value for a campaign under the active key. Stat columns read the
  // period rollup (falling back to the campaign document); name/group are
  // lower-cased strings; everything else falls through to the raw field.
  const sortValueFor = (c, key) => {
    const s = stat(c.id);
    switch (key) {
      case 'clicks':
      case 'total_clicks':
        return Number(s.clicks ?? c.total_clicks ?? 0);
      case 'conversions':
        return Number(s.conversions ?? c.conversions ?? 0);
      case 'cr':
        return Number(s.cr ?? 0);
      case 'revenue':
        return Number(s.revenue ?? c.revenue ?? 0);
      case 'cost':
        return Number(s.cost ?? 0);
      case 'profit':
        return Number(s.profit ?? 0);
      case 'status':
        return c.is_active ? 1 : 0;
      case 'created_at':
        return c.created_at || '';
      case 'group':
        return (c.group || '').toLowerCase();
      case 'name':
        return (c.name || '').toLowerCase();
      default: {
        const v = c[key];
        return typeof v === 'string' ? v.toLowerCase() : (v ?? '');
      }
    }
  };

  const sortedCampaigns = [...filteredCampaigns].sort((a, b) => {
    const va = sortValueFor(a, sortKey);
    const vb = sortValueFor(b, sortKey);
    if (va < vb) return sortDir === 'asc' ? -1 : 1;
    if (va > vb) return sortDir === 'asc' ? 1 : -1;
    return 0;
  });

  const exportCSV = () => {
    const rows = [['Name', 'Status', 'Clicks', 'Unique', 'Conversions', 'Revenue', 'CR%', 'Created']];
    sortedCampaigns.forEach((c) => {
      rows.push([
        c.name,
        c.is_active ? 'Active' : 'Paused',
        c.total_clicks || 0,
        c.unique_clicks || 0,
        c.conversions || 0,
        (Number(c.revenue) || 0).toFixed(2),
        c.total_clicks > 0 ? ((c.conversions / c.total_clicks) * 100).toFixed(2) : '0.00',
        c.created_at || '',
      ]);
    });
    const csv = rows.map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `campaigns-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(t('CSV exported'));
  };

  // Compact per-row action menu for the table view (mirrors the card menu,
  // minus the per-alias copy sub-list to keep the row tidy).
  const renderRowActions = (campaign) => (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" aria-label={t('Actions')}>
          <MoreHorizontal className="w-4 h-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem asChild>
          <Link to={`/campaigns/${campaign.id}`}>
            <Eye className="w-4 h-4 mr-2" />
            {t('More details')}
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => openEditDialog(campaign)}>
          <Edit className="w-4 h-4 mr-2" />
          {t('Edit')}
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => copyTrackingUrl(campaign)}>
          <Copy className="w-4 h-4 mr-2" />
          {t('Copy link (/r/)')}
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => handleCloneCampaign(campaign.id)}>
          <Copy className="w-4 h-4 mr-2" />
          {t('Clone')}
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => handleArchive(campaign)}>
          {campaign.is_archived ? (
            <>
              <ArchiveRestore className="w-4 h-4 mr-2" />
              {t('Restore')}
            </>
          ) : (
            <>
              <Archive className="w-4 h-4 mr-2" />
              {t('Archive')}
            </>
          )}
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => setDeleteTarget(campaign)} className="text-destructive">
          <Trash2 className="w-4 h-4 mr-2" />
          {t('Delete')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );

  const renderCampaignsTable = () => {
    const money = (n) => '$' + (Number(n) || 0).toFixed(2);
    const cols = [
      ['name', t('Name'), 'left'],
      ['status', t('Status'), 'left'],
      ['clicks', t('Clicks'), 'right'],
      ['conversions', t('Conversions'), 'right'],
      ['cr', t('CR %'), 'right'],
      ['revenue', t('Revenue'), 'right'],
      ['cost', t('Cost'), 'right'],
      ['profit', t('Profit'), 'right'],
      ['created_at', t('Created'), 'left'],
    ];
    return (
      <Card>
        <CardContent className="overflow-x-auto p-0">
          <Table data-testid="campaigns-table">
            <TableHeader>
              <TableRow>
                <TableHead className="w-8">
                  <input
                    type="checkbox"
                    className="xp-checkbox"
                    aria-label={t('Select all')}
                    checked={allVisibleSelected(sortedCampaigns)}
                    onChange={(e) => {
                      if (e.target.checked) selectAllVisible(sortedCampaigns);
                      else clearSelection();
                    }}
                  />
                </TableHead>
                {cols.map(([key, label, align]) => (
                  <TableHead
                    key={key}
                    className={
                      'cursor-pointer select-none ' + (align === 'right' ? 'text-right' : '')
                    }
                    onClick={() => toggleSort(key)}
                  >
                    <span
                      className={
                        'inline-flex items-center gap-1 ' +
                        (align === 'right' ? 'justify-end' : '')
                      }
                    >
                      {label}
                      <ArrowUpDown
                        className={
                          'w-3 h-3 ' + (sortKey === key ? 'opacity-100' : 'opacity-40')
                        }
                      />
                    </span>
                  </TableHead>
                ))}
                <TableHead className="w-10" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedCampaigns.map((campaign) => {
                const s = stat(campaign.id);
                const profit = Number(s.profit ?? 0);
                return (
                  <TableRow
                    key={campaign.id}
                    data-testid={`campaign-row-${campaign.id}`}
                    data-selected={selectedIds.has(campaign.id) ? 'true' : 'false'}
                    className={selectedIds.has(campaign.id) ? 'xp-card-selected' : ''}
                  >
                    <TableCell>
                      <input
                        type="checkbox"
                        className="xp-checkbox"
                        aria-label={t('Select campaign')}
                        checked={selectedIds.has(campaign.id)}
                        onChange={() => toggleSelected(campaign.id)}
                        data-testid={`campaign-row-select-${campaign.id}`}
                      />
                    </TableCell>
                    <TableCell className="font-medium">
                      <Link to={`/campaigns/${campaign.id}`} className="hover:underline">
                        {campaign.name}
                      </Link>
                      {campaign.group && (
                        <Badge variant="outline" className="ml-2 text-xs font-normal align-middle">
                          {campaign.group}
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant={campaign.is_active ? 'default' : 'secondary'}
                        className="cursor-pointer hover:opacity-80"
                        onClick={() => quickToggleStatus(campaign)}
                        title={campaign.is_active ? t('Click to pause') : t('Click to activate')}
                      >
                        {campaign.is_active ? t('Active') : t('Paused')}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {s.clicks ?? campaign.total_clicks ?? 0}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {s.conversions ?? campaign.conversions ?? 0}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {(Number(s.cr) || 0).toFixed(1)}%
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {money(s.revenue ?? campaign.revenue)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">{money(s.cost)}</TableCell>
                    <TableCell
                      className={
                        'text-right tabular-nums ' +
                        (profit > 0 ? 'text-emerald-500' : profit < 0 ? 'text-destructive' : '')
                      }
                    >
                      {money(profit)}
                    </TableCell>
                    <TableCell className="text-muted-foreground text-xs whitespace-nowrap">
                      {campaign.created_at ? timeAgo(campaign.created_at) : '—'}
                    </TableCell>
                    <TableCell className="text-right">{renderRowActions(campaign)}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="px-8 pt-2 pb-8 space-y-6">
      <XpnsrPageHeader
        kicker={t('Campaigns')}
        title={<span data-testid="campaigns-title">{t('Campaign management')}</span>}
        subtitle={t('Create, edit and track your ad campaigns in real time')}
        actions={
          <>
            <Select value={statsDays} onValueChange={setStatsDays}>
              <SelectTrigger className="w-32" data-testid="campaigns-period">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="1">{t('Today')}</SelectItem>
                <SelectItem value="7">{t('7 days')}</SelectItem>
                <SelectItem value="30">{t('30 days')}</SelectItem>
                <SelectItem value="90">{t('90 days')}</SelectItem>
              </SelectContent>
            </Select>
            <button
              type="button"
              className="xp-btn"
              onClick={exportCSV}
              title={t('Export campaigns to CSV')}
            >
              <Download style={{ width: 14, height: 14 }} />
              {t('Export CSV')}
            </button>
            <button
              type="button"
              className="xp-btn xp-btn-primary"
              data-testid="create-campaign-button"
              onClick={() => setShowCreateDialog(true)}
            >
              <Plus style={{ width: 14, height: 14 }} />
              {t('Create campaign')}
            </button>
          </>
        }
      />
      <Dialog open={showCreateDialog} onOpenChange={(open) => { setShowCreateDialog(open); if (!open) resetForm(); }}>
        <DialogContent className="sm:max-w-3xl w-[95vw] max-h-[90vh] overflow-y-auto overflow-x-hidden">
            <DialogHeader>
              <DialogTitle className="text-xl">{t('Create new campaign')}</DialogTitle>
              <DialogDescription>
                {t('Fill in the details of your new ad campaign')}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreateCampaign} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">{t('Campaign name')}</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder={t('For example: Product A ad')}
                  required
                  data-testid="campaign-name-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="target_url">{t('Target URL')} <HelpHint text={t("Default URL when no stream matched. Routing is usually driven by Streams — this field is a safety net.")} /></Label>
                <Input
                  id="target_url"
                  type="url"
                  value={formData.target_url}
                  onChange={(e) => setFormData({ ...formData, target_url: e.target.value })}
                  placeholder="https://example.com"
                  required
                  data-testid="campaign-url-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="traffic_source">{t('Traffic source')} <HelpHint text={t("The ad platform (Google Ads, Facebook…). Defines the set of macros/subs appended to the tracking link to pass click data.")} /></Label>
                <Select
                  value={formData.traffic_source_id || undefined}
                  onValueChange={(value) =>
                    setFormData({ ...formData, traffic_source_id: value || '' })
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder={t('Select a traffic source (optional)')} />
                  </SelectTrigger>
                  <SelectContent>
                    {trafficSources.map((source) => (
                      <SelectItem key={source.id} value={source.id}>
                        {source.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="domain_id">{t('Tracking domain')}</Label>
                <Select
                  value={formData.domain_id || '__default__'}
                  onValueChange={(value) =>
                    setFormData({ ...formData, domain_id: value === '__default__' ? '' : value })
                  }
                >
                  <SelectTrigger data-testid="campaign-domain-select">
                    <SelectValue placeholder={t('Workspace default domain')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__default__">{t('Workspace default domain')}</SelectItem>
                    {domains.map((d) => (
                      <SelectItem key={d.id} value={d.id}>
                        {d.hostname}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {domains.length === 0 ? (
                  <p className="text-xs text-muted-foreground">
                    {t('No verified custom domains. Add one in Domains → Settings.')}
                  </p>
                ) : null}
              </div>

              <details className="rounded-md border border-border p-3" data-testid="create-campaign-advanced">
                <summary className="cursor-pointer text-sm font-medium">{t('Additional settings')}</summary>
                <p className="mt-1 text-xs text-muted-foreground">
                  {t('Description, group, cost, click ID parameter, privacy, redirect mode, uniqueness and cloaking. The defaults work for most campaigns.')}
                </p>
                <div className="mt-3 space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="description">{t('Description (optional)')}</Label>
                    <Textarea
                      id="description"
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      placeholder={t('Short campaign description')}
                      rows={3}
                      data-testid="campaign-description-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="group">{t('Group (optional)')}</Label>
                    <Input
                      id="group"
                      value={formData.group}
                      onChange={(e) => setFormData({ ...formData, group: e.target.value })}
                      placeholder={t('e.g. Facebook, Google, Tier 1')}
                      list="campaign-groups"
                      data-testid="campaign-group-input"
                    />
                    {availableGroups.length > 0 && (
                      <datalist id="campaign-groups">
                        {availableGroups.map((g) => (
                          <option key={g} value={g} />
                        ))}
                      </datalist>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="cost_model">{t('Cost model')} <HelpHint text={t("How cost is counted: CPC — per click, CPM — per 1000 impressions, CPA — per conversion. Affects Cost / ROI / CPC / CPA in reports.")} /></Label>
                    <Select
                      value={formData.cost_model}
                      onValueChange={(value) => setFormData({ ...formData, cost_model: value })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="CPC">{t('CPC (per click)')}</SelectItem>
                        <SelectItem value="CPuC">{t('CPuC (per unique click)')}</SelectItem>
                        <SelectItem value="CPM">{t('CPM (per impression)')}</SelectItem>
                        <SelectItem value="CPA">{t('CPA (per action)')}</SelectItem>
                        <SelectItem value="CPS">{t('CPS (per sale)')}</SelectItem>
                        <SelectItem value="RevShare">{t('RevShare (% of payout)')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="traffic_loss_percent">{t('Traffic loss %')} <HelpHint text={t("Compensates cost for traffic lost between the source and the tracker: real cost = base / (1 − loss%/100).")} /></Label>
                    <Input
                      id="traffic_loss_percent"
                      type="number"
                      step="0.1"
                      min="0"
                      max="99"
                      value={formData.traffic_loss_percent}
                      onChange={(e) =>
                        setFormData({ ...formData, traffic_loss_percent: parseFloat(e.target.value) || 0 })
                      }
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    <div className="space-y-2">
                      <Label htmlFor="cost_value">{t('Cost value')}</Label>
                      <Input
                        id="cost_value"
                        type="number"
                        step="0.0001"
                        min="0"
                        value={formData.cost_value}
                        onChange={(e) =>
                          setFormData({ ...formData, cost_value: parseFloat(e.target.value) || 0 })
                        }
                        data-testid="campaign-cost-value-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="cost_currency">{t('Currency')}</Label>
                      <Select
                        value={formData.cost_currency}
                        onValueChange={(value) => setFormData({ ...formData, cost_currency: value })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="USD">USD</SelectItem>
                          <SelectItem value="EUR">EUR</SelectItem>
                          <SelectItem value="GBP">GBP</SelectItem>
                          <SelectItem value="RUB">RUB</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="click_id_param">{t('Click ID forward parameter')} <HelpHint text={t("The GET parameter name the tracker uses to append click_id to the offer URL (e.g. click_id → ?click_id=…). Needed so the partner returns this click_id in the postback. Form snippets read both click_id and clickid.")} /></Label>
                    <Input
                      id="click_id_param"
                      placeholder="clickid"
                      value={formData.click_id_param}
                      onChange={(e) =>
                        setFormData({ ...formData, click_id_param: e.target.value })
                      }
                      data-testid="campaign-click-id-param-input"
                    />
                    <p className="text-xs text-muted-foreground">
                      {t(
                        'When set, the tracker auto-appends ?<param>=<click_id> to redirect URLs. Leave empty to disable. Use {click_id} in the target URL for full control.'
                      )}
                    </p>
                  </div>

                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      className="xp-checkbox"
                      checked={formData.cookieless}
                      onChange={(e) =>
                        setFormData({ ...formData, cookieless: e.target.checked })
                      }
                      data-testid="campaign-cookieless-input"
                    />
                    <span>{t('Cookieless mode (skip xpnsr_vid first-party cookie)')}</span> <HelpHint text={t("Do not set the xpnsr_vid cookie (for networks that forbid cookies). Uniqueness is then by IP+User Agent, and click_id is passed via URL — the Click ID forward parameter field above.")} />
                  </label>

                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      className="xp-checkbox"
                      checked={formData.pseudonymize_ip}
                      onChange={(e) =>
                        setFormData({ ...formData, pseudonymize_ip: e.target.checked })
                      }
                      data-testid="campaign-pseudonymize-ip-input"
                    />
                    <span>{t('GDPR: pseudonymize visitor IPs (hash before storage)')}</span> <HelpHint text={t("Hash the IP before storing (for GDPR). GEO is resolved BEFORE hashing, so country/city targeting is unaffected.")} />
                  </label>

                  <div className="space-y-2">
                    <Label htmlFor="redirect_mode">{t('Redirect mode')} <HelpHint text={t("How to redirect: 302 — a plain fast redirect; Meta refresh / JS — when the network does not follow 302 or gtag is needed; Cloaked (iframe) — hide the offer URL in the address bar.")} /></Label>
                    <Select
                      value={formData.redirect_mode || '302'}
                      onValueChange={(value) =>
                        setFormData({ ...formData, redirect_mode: value })
                      }
                    >
                      <SelectTrigger data-testid="campaign-redirect-mode-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="302">{t('302 redirect (default)')}</SelectItem>
                        <SelectItem value="meta_refresh">{t('Meta refresh')}</SelectItem>
                        <SelectItem value="js">{t('JS redirect (location.replace)')}</SelectItem>
                        <SelectItem value="iframe">{t('Cloaked (iframe)')}</SelectItem>
                        <SelectItem value="double_meta">{t('Double meta (hide referrer)')}</SelectItem>
                        <SelectItem value="blank_referrer">{t('Blank referrer (JS)')}</SelectItem>
                        <SelectItem value="formsubmit">{t('Form submit (POST)')}</SelectItem>
                        <SelectItem value="curl">{t('Transparent (server-side fetch)')}</SelectItem>
                        <SelectItem value="remote">{t('Remote rotator (fetch target)')}</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">
                      {t(
                        'Cloaked iframe keeps the tracker domain in the address bar — only works for destinations that allow framing.'
                      )}
                    </p>
                    {formData.redirect_mode === 'iframe' ? (
                      <FrameCheck defaultUrl={formData.target_url || ''} />
                    ) : null}
                    {formData.redirect_mode === 'curl' ? (
                      <p className="text-xs text-amber-500" data-testid="campaign-curl-warning">
                        {t(
                          'Transparent mode fetches the page from our server, so the visitor IP and cookies never reach it. Use it only for your own landing pages, not for affiliate offer links, or conversions may not be counted.'
                        )}
                      </p>
                    ) : null}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="uniqueness_method">{t('Uniqueness method')} <HelpHint text={t("How returning visitors are identified for unique-click detection: Cookie (default), IP, IP+UA, or Parameter — identify by a GET parameter (e.g. external_id) so the same user is recognised across IP/cookie changes.")} /></Label>
                    <Select
                      value={formData.uniqueness_method || 'cookie'}
                      onValueChange={(value) => setFormData({ ...formData, uniqueness_method: value })}
                    >
                      <SelectTrigger data-testid="campaign-uniqueness-method-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="cookie">{t('Cookie (default)')}</SelectItem>
                        <SelectItem value="ip">{t('IP')}</SelectItem>
                        <SelectItem value="ip_ua">{t('IP + User-Agent')}</SelectItem>
                        <SelectItem value="parameter">{t('Parameter')}</SelectItem>
                      </SelectContent>
                    </Select>
                    {formData.uniqueness_method === 'parameter' && (
                      <Input
                        placeholder={t('Parameter name, e.g. external_id')}
                        value={formData.uniqueness_param || ''}
                        onChange={(e) => setFormData({ ...formData, uniqueness_param: e.target.value })}
                      />
                    )}
                  </div>

                  {renderCloakingSection('create')}
                </div>
              </details>

              <div className="flex justify-end space-x-2 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowCreateDialog(false)}
                >
                  {t('Cancel')}
                </Button>
                <Button type="submit" disabled={creating} data-testid="create-campaign-submit">
                  {creating ? t('Creating…') : t('Create campaign')}
                </Button>
              </div>
            </form>
        </DialogContent>
      </Dialog>

      {/* Search + Status filter + Select-all */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
          <Input
            placeholder={t('Search campaigns…')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
            data-testid="search-campaigns"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-36" data-testid="status-filter">
            <SelectValue placeholder={t('All statuses')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('All statuses')}</SelectItem>
            <SelectItem value="active">{t('Active')}</SelectItem>
            <SelectItem value="paused">{t('Paused')}</SelectItem>
            <SelectItem value="archived">{t('Archived')}</SelectItem>
          </SelectContent>
        </Select>
        {availableGroups.length > 0 && (
          <Select value={groupFilter} onValueChange={setGroupFilter}>
            <SelectTrigger className="w-36" data-testid="group-filter">
              <SelectValue placeholder={t('All groups')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('All groups')}</SelectItem>
              <SelectItem value="__none__">{t('Ungrouped')}</SelectItem>
              {availableGroups.map((g) => (
                <SelectItem key={g} value={g}>{g}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        <Select value={`${sortKey}:${sortDir}`} onValueChange={(v) => { const [k, d] = v.split(':'); setSortKey(k); setSortDir(d); }}>
          <SelectTrigger className="w-40" data-testid="sort-select">
            <SelectValue placeholder={t('Sort by')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="name:asc">{t('Name A→Z')}</SelectItem>
            <SelectItem value="name:desc">{t('Name Z→A')}</SelectItem>
            <SelectItem value="created_at:desc">{t('Newest first')}</SelectItem>
            <SelectItem value="created_at:asc">{t('Oldest first')}</SelectItem>
            <SelectItem value="total_clicks:desc">{t('Most clicks')}</SelectItem>
            <SelectItem value="revenue:desc">{t('Most revenue')}</SelectItem>
          </SelectContent>
        </Select>
        <div
          className="inline-flex rounded-md border border-border overflow-hidden"
          data-testid="campaigns-view-toggle"
        >
          <button
            type="button"
            onClick={() => changeView('cards')}
            className={'xp-btn' + (viewMode === 'cards' ? ' xp-btn-primary' : '')}
            aria-label={t('Card view')}
            title={t('Card view')}
            aria-pressed={viewMode === 'cards'}
            data-testid="campaigns-view-cards"
          >
            <LayoutDashboard style={{ width: 14, height: 14 }} />
          </button>
          <button
            type="button"
            onClick={() => changeView('table')}
            className={'xp-btn' + (viewMode === 'table' ? ' xp-btn-primary' : '')}
            aria-label={t('Table view')}
            title={t('Table view')}
            aria-pressed={viewMode === 'table'}
            data-testid="campaigns-view-table"
          >
            <List style={{ width: 14, height: 14 }} />
          </button>
        </div>
        <button
          type="button"
          className="xp-btn"
          onClick={() => setSaveViewOpen(true)}
          title={t('Save current view')}
          data-testid="save-view-btn"
        >
          <Save style={{ width: 14, height: 14 }} />
          {t('Save view')}
        </button>
        {sortedCampaigns.length > 0 ? (
          <label className="xp-bulk-select-all" data-testid="campaigns-select-all">
            <input
              type="checkbox"
              className="xp-checkbox"
              checked={allVisibleSelected(sortedCampaigns)}
              onChange={(e) => {
                if (e.target.checked) selectAllVisible(sortedCampaigns);
                else clearSelection();
              }}
            />
            {allVisibleSelected(sortedCampaigns)
              ? t('Deselect all')
              : t('Select all')}
            <span className="xp-bulk-select-count">
              {sortedCampaigns.length}
            </span>
          </label>
        ) : null}
      </div>

      {savedViews.length > 0 && (
        <div className="flex flex-wrap items-center gap-2" data-testid="saved-views">
          <span className="text-xs text-muted-foreground">{t('Saved views')}:</span>
          {savedViews.map((v) => (
            <span
              key={v.name}
              className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-surface-2 border border-border rounded-md"
            >
              <button type="button" className="hover:underline" onClick={() => applyView(v)}>
                {v.name}
              </button>
              <button
                type="button"
                aria-label={t('Delete view')}
                className="opacity-50 hover:opacity-100"
                onClick={() => deleteView(v.name)}
              >
                <X style={{ width: 11, height: 11 }} />
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Bulk action toolbar (only when N selected) */}
      {selectedIds.size > 0 ? (
        <div className="xp-bulk-toolbar" data-testid="campaigns-bulk-toolbar">
          <div className="xp-bulk-toolbar-count">
            <span className="xp-bulk-toolbar-dot" />
            {t('{n} selected').replace('{n}', String(selectedIds.size))}
          </div>
          <div className="xp-bulk-toolbar-actions">
            <button
              type="button"
              className="xp-btn"
              onClick={() => bulkSetActive(true)}
              disabled={bulkBusy}
              data-testid="bulk-activate"
            >
              <Play style={{ width: 12, height: 12 }} />
              {t('Activate')}
            </button>
            <button
              type="button"
              className="xp-btn"
              onClick={() => bulkSetActive(false)}
              disabled={bulkBusy}
              data-testid="bulk-pause"
            >
              <Pause style={{ width: 12, height: 12 }} />
              {t('Pause')}
            </button>
            <button
              type="button"
              className="xp-btn xp-btn-danger"
              onClick={() => setBulkDeleteOpen(true)}
              disabled={bulkBusy}
              data-testid="bulk-delete"
            >
              <Trash2 style={{ width: 12, height: 12 }} />
              {t('Delete')}
            </button>
            <button
              type="button"
              className="xp-btn xp-btn-clear"
              onClick={clearSelection}
              disabled={bulkBusy}
              data-testid="bulk-clear"
            >
              <X style={{ width: 12, height: 12 }} />
              {t('Clear')}
            </button>
          </div>
        </div>
      ) : null}

      {/* Campaigns List */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[...Array(6)].map((_, i) => (
            <Card key={i} className="skeleton h-64"></Card>
          ))}
        </div>
      ) : sortedCampaigns.length === 0 ? (
        <Card>
          <CardContent style={{ padding: 0 }}>
            <XpnsrEmpty
              icon={Plus}
              title={campaigns.length === 0 ? t('No campaigns') : t('Campaigns not found')}
              subtitle={
                campaigns.length === 0
                  ? t('Create your first campaign to start tracking traffic')
                  : t('Try adjusting search criteria')
              }
              cta={
                campaigns.length === 0 ? (
                  <button
                    type="button"
                    className="xp-btn xp-btn-primary"
                    onClick={openCreateDialog}
                  >
                    {t('Create your first campaign')}
                  </button>
                ) : null
              }
            />
          </CardContent>
        </Card>
      ) : (
        <>
          {(() => {
            const tt = sortedCampaigns.reduce(
              (a, cc) => {
                const s = summaryMap[cc.id] || {};
                a.clicks += Number(s.clicks) || 0;
                a.conv += Number(s.conversions) || 0;
                a.revenue += Number(s.revenue) || 0;
                a.cost += Number(s.cost) || 0;
                return a;
              },
              { clicks: 0, conv: 0, revenue: 0, cost: 0 },
            );
            const profit = tt.revenue - tt.cost;
            const cr = tt.clicks > 0 ? (tt.conv / tt.clicks) * 100 : 0;
            const money = (n) => '$' + Number(n || 0).toFixed(0);
            const tiles = [
              [t('Clicks'), String(tt.clicks)],
              [t('Conversions'), String(tt.conv)],
              [t('CR'), cr.toFixed(1) + '%'],
              [t('Rev.'), money(tt.revenue)],
              [t('Cost'), money(tt.cost)],
              [t('Profit'), money(profit)],
            ];
            return (
              <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 mb-4" data-testid="campaigns-totals">
                {tiles.map(([label, value]) => (
                  <div key={label} className="text-center px-2 py-2 bg-surface-2 border border-border rounded-md">
                    <div className="text-sm font-semibold text-foreground tabular-nums">{value}</div>
                    <div className="text-xs text-muted-foreground">{label}</div>
                  </div>
                ))}
              </div>
            );
          })()}
        {viewMode === 'cards' && (
        <div className="xp-card-grid grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {sortedCampaigns.map((campaign) => (
            <Card
              key={campaign.id}
              className={`card-hover ${selectedIds.has(campaign.id) ? 'xp-card-selected' : ''}`.trim()}
              data-testid={`campaign-card-${campaign.id}`}
              data-selected={selectedIds.has(campaign.id) ? 'true' : 'false'}
            >
              <CardHeader className="pb-3">
                <div className="flex justify-between items-start gap-3">
                  <label
                    className="xp-checkbox-wrap"
                    aria-label={t('Select campaign')}
                  >
                    <input
                      type="checkbox"
                      className="xp-checkbox"
                      checked={selectedIds.has(campaign.id)}
                      onChange={() => toggleSelected(campaign.id)}
                      data-testid={`campaign-select-${campaign.id}`}
                    />
                  </label>
                  <div className="flex-1 min-w-0">
                    <CardTitle className="text-lg mb-1 break-words">
                      {campaign.name}
                      {campaign.group && (
                        <Badge variant="outline" className="ml-2 text-xs font-normal align-middle">{campaign.group}</Badge>
                      )}
                    </CardTitle>
                    <CardDescription className="line-clamp-2">
                      {campaign.description || t('No description')}
                    </CardDescription>
                    {campaign.created_at && (
                      <span className="text-[10px] text-muted-foreground opacity-60">{timeAgo(campaign.created_at)}</span>
                    )}
                  </div>
                  <div className="flex items-center space-x-1 shrink-0">
                    <Badge
                      variant={campaign.is_active ? 'default' : 'secondary'}
                      className="cursor-pointer hover:opacity-80 transition-opacity whitespace-nowrap"
                      onClick={() => quickToggleStatus(campaign)}
                      title={campaign.is_active ? t('Click to pause') : t('Click to activate')}
                    >
                      {campaign.is_active ? t('Active') : t('Paused')}
                    </Badge>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm" aria-label={t('Campaign actions')} title={t('Campaign actions')}>
                          <MoreHorizontal className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem asChild>
                          <Link to={`/campaigns/${campaign.id}`}>
                            <Eye className="w-4 h-4 mr-2" />
                            {t('More details')}
                          </Link>
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => openEditDialog(campaign)}>
                          <Edit className="w-4 h-4 mr-2" />
                          {t('Edit')}
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => copyTrackingUrl(campaign)}>
                          <Copy className="w-4 h-4 mr-2" />
                          {t('Copy link (/r/)')}
                        </DropdownMenuItem>
                        {TRACKER_ALIASES.filter((a) => a !== 'r').map((alias) => (
                          <DropdownMenuItem
                            key={alias}
                            onClick={() => copyTrackingUrl(campaign, alias)}
                            data-testid={`copy-tracking-url-alias-${alias}-${campaign.id}`}
                          >
                            <Copy className="w-4 h-4 mr-2" />
                            {t('Copy link (/{alias}/)').replace('{alias}', alias)}
                          </DropdownMenuItem>
                        ))}
                        <DropdownMenuItem onClick={() => handleCloneCampaign(campaign.id)}>
                          <Copy className="w-4 h-4 mr-2" />
                          {t('Clone')}
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleArchive(campaign)}>
                          {campaign.is_archived
                            ? <><ArchiveRestore className="w-4 h-4 mr-2" />{t('Restore')}</>
                            : <><Archive className="w-4 h-4 mr-2" />{t('Archive')}</>
                          }
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => setDeleteTarget(campaign)}
                          className="text-destructive"
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          {t('Delete')}
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {/* Stats (period-aware) */}
                <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3 mb-4">
                  <div className="text-center">
                    <div className="flex items-center justify-center w-8 h-8 bg-surface-2 border border-border rounded-md mx-auto mb-1">
                      <MousePointer className="w-4 h-4 text-primary" />
                    </div>
                    <div className="text-sm font-medium text-foreground tabular-nums">{stat(campaign.id).clicks ?? campaign.total_clicks}</div>
                    <div className="text-xs text-muted-foreground">{t('Clicks')}</div>
                  </div>
                  <div className="text-center">
                    <div className="flex items-center justify-center w-8 h-8 bg-surface-2 border border-border rounded-md mx-auto mb-1">
                      <TrendingUp className="w-4 h-4 text-primary" />
                    </div>
                    <div className="text-sm font-medium text-foreground tabular-nums">{stat(campaign.id).conversions ?? campaign.conversions}</div>
                    <div className="text-xs text-muted-foreground">{t('Conversions')}</div>
                  </div>
                  <div className="text-center">
                    <div className="flex items-center justify-center w-8 h-8 bg-surface-2 border border-border rounded-md mx-auto mb-1">
                      <Target className="w-4 h-4 text-primary" />
                    </div>
                    <div className="text-sm font-medium text-foreground tabular-nums">
                      {stat(campaign.id).cr != null
                        ? Number(stat(campaign.id).cr).toFixed(1) + '%'
                        : campaign.total_clicks > 0
                          ? ((campaign.conversions / campaign.total_clicks) * 100).toFixed(1) + '%'
                          : '—'}
                    </div>
                    <div className="text-xs text-muted-foreground">{t('CR')}</div>
                  </div>
                  <div className="text-center">
                    <div className="flex items-center justify-center w-8 h-8 bg-surface-2 border border-border rounded-md mx-auto mb-1">
                      <DollarSign className="w-4 h-4 text-primary" />
                    </div>
                    <div className="text-sm font-medium text-foreground tabular-nums">
                      {'$' + (Number(stat(campaign.id).revenue ?? campaign.revenue) || 0).toFixed(0)}
                    </div>
                    <div className="text-xs text-muted-foreground">{t('Rev.')}</div>
                  </div>
                  <div className="text-center">
                    <div className="flex items-center justify-center w-8 h-8 bg-surface-2 border border-border rounded-md mx-auto mb-1">
                      <DollarSign className="w-4 h-4 text-muted-foreground" />
                    </div>
                    <div className="text-sm font-medium text-foreground tabular-nums">
                      {'$' + (Number(stat(campaign.id).cost) || 0).toFixed(0)}
                    </div>
                    <div className="text-xs text-muted-foreground">{t('Cost')}</div>
                  </div>
                </div>

                {/* Tracking Info */}
                <div className="bg-surface-2 border border-border rounded-md p-3 mb-4">
                  <div className="text-xs text-muted-foreground mb-1 font-mono uppercase tracking-[0.15em]">
                    {t('Tracking ID')}
                  </div>
                  <div className="flex items-center justify-between">
                    <code className="text-sm font-mono bg-background px-2 py-1 rounded border border-border">
                      {campaign.tracking_id}
                    </code>
                    <Button
                      size="sm"
                      variant="ghost"
                      aria-label={t('Copy tracking link')}
                      title={t('Copy tracking link')}
                      onClick={() => copyTrackingUrl(campaign)}
                      data-testid={`copy-tracking-url-${campaign.id}`}
                    >
                      <Copy className="w-3 h-3" />
                    </Button>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex space-x-2">
                  <Link
                    to={`/campaigns/${campaign.id}`}
                    style={{
                      display: 'inline-flex', alignItems: 'center', flex: 1,
                      justifyContent: 'center', padding: '7px 14px', fontSize: '0.8125rem', fontWeight: 500, borderRadius: '0.75rem',
                      border: '1px solid rgb(var(--border-2))', color: 'rgb(var(--text))',
                      textDecoration: 'none', fontFamily: 'var(--font-sans)',
                      transition: 'border-color 0.15s',
                    }}
                  >
                    <Eye style={{ width: 12, height: 12, marginRight: 4 }} />
                    {t('More details')}
                  </Link>
                  <Button
                    variant="outline"
                    size="sm"
                    aria-label={t('Open target URL')}
                    title={t('Open target URL')}
                    onClick={() => window.open(campaign.target_url, '_blank', 'noopener,noreferrer')}
                  >
                    <ExternalLink className="w-3 h-3" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
        )}
        {viewMode === 'table' && renderCampaignsTable()}
        </>
      )}

      {/* Save View Dialog */}
      <Dialog open={saveViewOpen} onOpenChange={(open) => { setSaveViewOpen(open); if (!open) setNewViewName(''); }}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>{t('Save current view')}</DialogTitle>
            <DialogDescription>
              {t('Remember the current filters, sort and layout under a name for one-click recall.')}
            </DialogDescription>
          </DialogHeader>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              saveCurrentView();
            }}
            className="space-y-4"
          >
            <div className="space-y-2">
              <Label htmlFor="view-name">{t('View name')}</Label>
              <Input
                id="view-name"
                value={newViewName}
                onChange={(e) => setNewViewName(e.target.value)}
                placeholder={t('e.g. Tier-1 by revenue')}
                autoFocus
                data-testid="view-name-input"
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setSaveViewOpen(false)}>
                {t('Cancel')}
              </Button>
              <Button type="submit" disabled={!newViewName.trim()} data-testid="save-view-submit">
                {t('Save view')}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Campaign Dialog */}
      <Dialog open={showEditDialog} onOpenChange={(open) => { setShowEditDialog(open); if (!open) { setEditingCampaign(null); resetForm(); } }}>
        <DialogContent className="sm:max-w-3xl w-[95vw] max-h-[90vh] overflow-y-auto overflow-x-hidden">
          <DialogHeader>
            <DialogTitle>{t('Edit campaign')}</DialogTitle>
            <DialogDescription>{t('Update campaign details')}</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleUpdateCampaign} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="edit-name">{t('Campaign name')}</Label>
              <Input
                id="edit-name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder={t('For example: Product A ad')}
                required
                data-testid="edit-campaign-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-target_url">{t('Target URL')} <HelpHint text={t("Default URL when no stream matched. Routing is usually driven by Streams — this field is a safety net.")} /></Label>
              <Input
                id="edit-target_url"
                type="url"
                value={formData.target_url}
                onChange={(e) => setFormData({ ...formData, target_url: e.target.value })}
                placeholder="https://example.com"
                required
                data-testid="edit-campaign-url-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-traffic_source">{t('Traffic source')} <HelpHint text={t("The ad platform (Google Ads, Facebook…). Defines the set of macros/subs appended to the tracking link to pass click data.")} /></Label>
              <Select
                value={formData.traffic_source_id || undefined}
                onValueChange={(value) =>
                  setFormData({ ...formData, traffic_source_id: value || '' })
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder={t('Select a traffic source (optional)')} />
                </SelectTrigger>
                <SelectContent>
                  {trafficSources.map((source) => (
                    <SelectItem key={source.id} value={source.id}>
                      {source.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-domain_id">{t('Tracking domain')}</Label>
              <Select
                value={formData.domain_id || '__default__'}
                onValueChange={(value) =>
                  setFormData({ ...formData, domain_id: value === '__default__' ? '' : value })
                }
              >
                <SelectTrigger data-testid="edit-campaign-domain-select">
                  <SelectValue placeholder={t('Workspace default domain')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__default__">{t('Workspace default domain')}</SelectItem>
                  {domains.map((d) => (
                    <SelectItem key={d.id} value={d.id}>
                      {d.hostname}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {domains.length === 0 ? (
                <p className="text-xs text-muted-foreground">
                  {t('No verified custom domains. Add one in Domains → Settings.')}
                </p>
              ) : null}
            </div>

            <details className="rounded-md border border-border p-3" data-testid="edit-campaign-advanced">
              <summary className="cursor-pointer text-sm font-medium">{t('Additional settings')}</summary>
              <p className="mt-1 text-xs text-muted-foreground">
                {t('Description, group, cost, click ID parameter, privacy, redirect mode, uniqueness and cloaking. The defaults work for most campaigns.')}
              </p>
              <div className="mt-3 space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-description">{t('Description (optional)')}</Label>
                  <Textarea
                    id="edit-description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder={t('Short campaign description')}
                    rows={3}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="edit-group">{t('Group (optional)')}</Label>
                  <Input
                    id="edit-group"
                    value={formData.group}
                    onChange={(e) => setFormData({ ...formData, group: e.target.value })}
                    placeholder={t('e.g. Facebook, Google, Tier 1')}
                    list="campaign-groups-edit"
                    data-testid="edit-campaign-group-input"
                  />
                  {availableGroups.length > 0 && (
                    <datalist id="campaign-groups-edit">
                      {availableGroups.map((g) => (
                        <option key={g} value={g} />
                      ))}
                    </datalist>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="edit-cost_model">{t('Cost model')} <HelpHint text={t("How cost is counted: CPC — per click, CPM — per 1000 impressions, CPA — per conversion. Affects Cost / ROI / CPC / CPA in reports.")} /></Label>
                  <Select
                    value={formData.cost_model}
                    onValueChange={(value) => setFormData({ ...formData, cost_model: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="CPC">{t('CPC (per click)')}</SelectItem>
                      <SelectItem value="CPuC">{t('CPuC (per unique click)')}</SelectItem>
                      <SelectItem value="CPM">{t('CPM (per impression)')}</SelectItem>
                      <SelectItem value="CPA">{t('CPA (per action)')}</SelectItem>
                      <SelectItem value="CPS">{t('CPS (per sale)')}</SelectItem>
                      <SelectItem value="RevShare">{t('RevShare (% of payout)')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="edit-traffic_loss_percent">{t('Traffic loss %')} <HelpHint text={t("Compensates cost for traffic lost between the source and the tracker: real cost = base / (1 − loss%/100).")} /></Label>
                  <Input
                    id="edit-traffic_loss_percent"
                    type="number"
                    step="0.1"
                    min="0"
                    max="99"
                    value={formData.traffic_loss_percent}
                    onChange={(e) =>
                      setFormData({ ...formData, traffic_loss_percent: parseFloat(e.target.value) || 0 })
                    }
                  />
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-2">
                    <Label htmlFor="edit-cost_value">{t('Cost value')}</Label>
                    <Input
                      id="edit-cost_value"
                      type="number"
                      step="0.0001"
                      min="0"
                      value={formData.cost_value}
                      onChange={(e) =>
                        setFormData({ ...formData, cost_value: parseFloat(e.target.value) || 0 })
                      }
                      data-testid="edit-campaign-cost-value-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="edit-cost_currency">{t('Currency')}</Label>
                    <Select
                      value={formData.cost_currency}
                      onValueChange={(value) => setFormData({ ...formData, cost_currency: value })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="USD">USD</SelectItem>
                        <SelectItem value="EUR">EUR</SelectItem>
                        <SelectItem value="GBP">GBP</SelectItem>
                        <SelectItem value="RUB">RUB</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="edit-click_id_param">{t('Click ID forward parameter')} <HelpHint text={t("The GET parameter name the tracker uses to append click_id to the offer URL (e.g. click_id → ?click_id=…). Needed so the partner returns this click_id in the postback. Form snippets read both click_id and clickid.")} /></Label>
                  <Input
                    id="edit-click_id_param"
                    placeholder="clickid"
                    value={formData.click_id_param}
                    onChange={(e) =>
                      setFormData({ ...formData, click_id_param: e.target.value })
                    }
                    data-testid="edit-campaign-click-id-param-input"
                  />
                  <p className="text-xs text-muted-foreground">
                    {t(
                      'When set, the tracker auto-appends ?<param>=<click_id> to redirect URLs. Leave empty to disable. Use {click_id} in the target URL for full control.'
                    )}
                  </p>
                </div>

                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    className="xp-checkbox"
                    checked={formData.cookieless}
                    onChange={(e) =>
                      setFormData({ ...formData, cookieless: e.target.checked })
                    }
                    data-testid="edit-campaign-cookieless-input"
                  />
                  <span>{t('Cookieless mode (skip xpnsr_vid first-party cookie)')}</span> <HelpHint text={t("Do not set the xpnsr_vid cookie (for networks that forbid cookies). Uniqueness is then by IP+User Agent, and click_id is passed via URL — the Click ID forward parameter field above.")} />
                </label>

                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    className="xp-checkbox"
                    checked={formData.pseudonymize_ip}
                    onChange={(e) =>
                      setFormData({ ...formData, pseudonymize_ip: e.target.checked })
                    }
                    data-testid="edit-campaign-pseudonymize-ip-input"
                  />
                  <span>{t('GDPR: pseudonymize visitor IPs (hash before storage)')}</span> <HelpHint text={t("Hash the IP before storing (for GDPR). GEO is resolved BEFORE hashing, so country/city targeting is unaffected.")} />
                </label>

                <div className="space-y-2">
                  <Label htmlFor="edit-redirect_mode">{t('Redirect mode')} <HelpHint text={t("How to redirect: 302 — a plain fast redirect; Meta refresh / JS — when the network does not follow 302 or gtag is needed; Cloaked (iframe) — hide the offer URL in the address bar.")} /></Label>
                  <Select
                    value={formData.redirect_mode || '302'}
                    onValueChange={(value) =>
                      setFormData({ ...formData, redirect_mode: value })
                    }
                  >
                    <SelectTrigger data-testid="edit-campaign-redirect-mode-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="302">{t('302 redirect (default)')}</SelectItem>
                      <SelectItem value="meta_refresh">{t('Meta refresh')}</SelectItem>
                      <SelectItem value="js">{t('JS redirect (location.replace)')}</SelectItem>
                      <SelectItem value="iframe">{t('Cloaked (iframe)')}</SelectItem>
                      <SelectItem value="double_meta">{t('Double meta (hide referrer)')}</SelectItem>
                      <SelectItem value="blank_referrer">{t('Blank referrer (JS)')}</SelectItem>
                      <SelectItem value="formsubmit">{t('Form submit (POST)')}</SelectItem>
                      <SelectItem value="curl">{t('Transparent (server-side fetch)')}</SelectItem>
                      <SelectItem value="remote">{t('Remote rotator (fetch target)')}</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    {t(
                      'Cloaked iframe keeps the tracker domain in the address bar — only works for destinations that allow framing.'
                    )}
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="edit-uniqueness_method">{t('Uniqueness method')} <HelpHint text={t("How returning visitors are identified for unique-click detection: Cookie (default), IP, IP+UA, or Parameter — identify by a GET parameter (e.g. external_id) so the same user is recognised across IP/cookie changes.")} /></Label>
                  <Select
                    value={formData.uniqueness_method || 'cookie'}
                    onValueChange={(value) => setFormData({ ...formData, uniqueness_method: value })}
                  >
                    <SelectTrigger data-testid="edit-campaign-uniqueness-method-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="cookie">{t('Cookie (default)')}</SelectItem>
                      <SelectItem value="ip">{t('IP')}</SelectItem>
                      <SelectItem value="ip_ua">{t('IP + User-Agent')}</SelectItem>
                      <SelectItem value="parameter">{t('Parameter')}</SelectItem>
                    </SelectContent>
                  </Select>
                  {formData.uniqueness_method === 'parameter' && (
                    <Input
                      placeholder={t('Parameter name, e.g. external_id')}
                      value={formData.uniqueness_param || ''}
                      onChange={(e) => setFormData({ ...formData, uniqueness_param: e.target.value })}
                    />
                  )}
                </div>

                {renderCloakingSection('edit')}
              </div>
            </details>

            <div className="flex justify-end space-x-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setShowEditDialog(false)}>
                {t('Cancel')}
              </Button>
              <Button type="submit" disabled={saving} data-testid="edit-campaign-submit">
                {saving ? t('Saving…') : t('Save changes')}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}
        title={t('Delete campaign')}
        description={t('This will permanently delete "{name}" and all associated clicks, conversions and postback events. This cannot be undone.').replace('{name}', deleteTarget?.name || '')}
        confirmLabel={t('Delete campaign')}
        onConfirm={() => handleDeleteCampaign(deleteTarget?.id)}
      />
      <ConfirmDialog
        open={bulkDeleteOpen}
        onOpenChange={setBulkDeleteOpen}
        title={t('Delete campaigns')}
        description={t('Delete {n} selected campaign(s)? This cannot be undone.').replace('{n}', String(selectedIds.size))}
        confirmLabel={t('Delete')}
        onConfirm={bulkDelete}
      />
    </div>
  );
}
